import { useState, useRef } from 'react';
import { Upload, File, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { uploadDocument, formatFileSize } from '../../services/storage';
import { useAuth } from '../../context/AuthContext';
import { usePostHog } from '@posthog/react';

export default function UploadDropzone({ projectId, onUploadComplete }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]);
  const fileInputRef = useRef(null);
  const { currentUser } = useAuth();
  const posthog = usePostHog();

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    // Reset input
    e.target.value = '';
  };

  const handleFiles = (files) => {
    // Filter for PDF files only
    const pdfFiles = files.filter(file => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      alert('Please select PDF files only');
      return;
    }

    // Add files to uploads list
    const newUploads = pdfFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'uploading', // uploading, completed, error
      error: null,
    }));

    setUploads(prev => [...prev, ...newUploads]);

    // Start uploading each file
    newUploads.forEach(upload => startUpload(upload));
  };

  const startUpload = async (upload) => {
    try {
      const result = await uploadDocument(
        upload.file,
        currentUser.uid,
        (progress) => {
          // Update progress
          setUploads(prev => prev.map(u =>
            u.id === upload.id ? { ...u, progress } : u
          ));
        }
      );

      if (result.success) {
        // Mark as completed
        setUploads(prev => prev.map(u =>
          u.id === upload.id ? { ...u, status: 'completed', progress: 100 } : u
        ));

        posthog?.capture('document_uploaded', {
          project_id: projectId,
          file_name: result.fileName,
          file_size_bytes: result.fileSize,
        });

        // Notify parent component
        if (onUploadComplete) {
          onUploadComplete({
            projectId,
            name: result.fileName,
            storagePath: result.path,
            downloadURL: result.url,
            fileSize: result.fileSize,
          });
        }

        // Remove from list after 2 seconds
        setTimeout(() => {
          setUploads(prev => prev.filter(u => u.id !== upload.id));
        }, 2000);
      } else {
        // Mark as error
        setUploads(prev => prev.map(u =>
          u.id === upload.id ? { ...u, status: 'error', error: result.error } : u
        ));
      }
    } catch (error) {
      console.error('Upload error:', error);
      posthog?.captureException(error);
      setUploads(prev => prev.map(u =>
        u.id === upload.id ? { ...u, status: 'error', error: 'Upload failed. Please try again.' } : u
      ));
    }
  };

  const removeUpload = (uploadId) => {
    setUploads(prev => prev.filter(u => u.id !== uploadId));
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition
          ${isDragging
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-gray-300 hover:border-emerald-400 hover:bg-gray-50'
          }
        `}
      >
        <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-emerald-600' : 'text-gray-400'}`} />
        <p className="text-lg font-medium text-gray-900 mb-2">
          {isDragging ? 'Drop files here' : 'Drop PDF files here'}
        </p>
        <p className="text-sm text-gray-600 mb-4">
          or click to browse
        </p>
        <p className="text-xs text-gray-500">
          PDF files only • Max 50MB per file
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Upload Progress List */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          {uploads.map(upload => (
            <div
              key={upload.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="flex-shrink-0">
                  {upload.status === 'completed' ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : upload.status === 'error' ? (
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  ) : (
                    <File className="w-6 h-6 text-blue-600" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {upload.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(upload.size)}
                  </p>

                  {/* Progress Bar */}
                  {upload.status === 'uploading' && (
                    <div className="mt-2">
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-600 transition-all duration-300"
                          style={{ width: `${upload.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {upload.progress}% uploaded
                      </p>
                    </div>
                  )}

                  {/* Error Message */}
                  {upload.status === 'error' && (
                    <p className="text-xs text-red-600 mt-1">
                      {upload.error || 'Upload failed'}
                    </p>
                  )}

                  {/* Success Message */}
                  {upload.status === 'completed' && (
                    <p className="text-xs text-green-600 mt-1">
                      Upload completed
                    </p>
                  )}
                </div>

                {/* Remove Button */}
                {(upload.status === 'error' || upload.status === 'completed') && (
                  <button
                    onClick={() => removeUpload(upload.id)}
                    className="flex-shrink-0 p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
